import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { LogIn, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { LOGO_URL } from "@/lib/feedModels";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(`Selamat datang, ${u.name}`);
      navigate(u.role === "pegawai" ? "/data-sapi" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10">
      <div
        data-testid="login-card"
        className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-[#E0A988]/40 p-8"
      >
        <div className="flex flex-col items-center text-center mb-6">
          <img src={LOGO_URL} alt="Sembada Satwa" className="w-20 h-20 object-contain mb-2" />
          <h1 className="font-display text-2xl font-extrabold text-[#8A5230]">Login Admin</h1>
          <p className="text-sm text-[#A06E4D]">Masuk untuk mengelola data sapi & biaya</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="login-username" className="text-sm font-semibold text-[#7A4A28]">
              Username
            </Label>
            <Input
              id="login-username"
              data-testid="input-login-username"
              type="text"
              required
              placeholder="admin"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="h-12 bg-[#FBF3E8] border-[#E0A988]/60 focus-visible:ring-[#A06E4D]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="login-password" className="text-sm font-semibold text-[#7A4A28]">
              Password
            </Label>
            <div className="relative">
              <Input
                id="login-password"
                data-testid="input-login-password"
                type={showPass ? "text" : "password"}
                required
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 pr-12 bg-[#FBF3E8] border-[#E0A988]/60 focus-visible:ring-[#A06E4D]"
              />
              <button
                type="button"
                data-testid="toggle-login-password"
                onClick={() => setShowPass((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A06E4D]"
              >
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid="btn-login-submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-[#A06E4D] hover:bg-[#8A5230] text-white font-semibold"
          >
            <LogIn className="w-4 h-4 mr-2" />
            {loading ? "Memproses..." : "Masuk"}
          </Button>
        </form>
      </div>
    </div>
  );
}
